import React, { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'

import { DINING_METHOD, extractInventoryIds, PAYMENT_METHOD, PAYMENT_METHOD_OPTIONS, ROUTES } from '@common'
import { Loader, DiningMethodEditModal } from '@components'
import { useCartRedux, useOrderRedux, useUserRedux } from '@hooks'
import InventoryWarningModal from '../InventoryWarningModal'
// import PaymentMethodEditModal from '../PaymentMethodEditModal'

const CartFooter = () => {
    const navigate = useNavigate()
    const { items, checkInventoryStatus, inventoryChanges, clientRequestId, initClientRequestId } = useCartRedux()
    const { isLoading, submitOrder } = useOrderRedux()
    const { diningMethod, tableId } = useUserRedux()
    const isEmpty = useMemo(() => items.length === 0, [items])
    const [isEditModalOpen, setIsEditModalOpen] = useState(false)
    const [isInventoryModalOpen, setIsInventoryModalOpen] = useState(false)
    // const [isPaymentMethodModalOpen, setIsPaymentMethodModalOpen] = useState(false)
    // const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHOD_OPTIONS[0])
    const paymentMethod = PAYMENT_METHOD_OPTIONS[0]
    const [isSubmitting, setIsSubmitting] = useState(false)
    const isProcessing = useMemo(() => isSubmitting || isLoading || isEmpty, [isSubmitting, isLoading, isEmpty])

    useEffect(() => {
        initClientRequestId()
        // eslint-disable-next-line
    }, [])

    const total = useMemo(() => {
        const { price, count } = items.reduce(
            (acc, item) => {
                const selectionPrice = item.selections.reduce((acc, selection) => {
                    const selectedOptions = selection.options
                    return (
                        acc + selectedOptions.reduce((optionTotal, option) => optionTotal + option.sellPrice * option.quantity, 0)
                    )
                }, 0)

                const { variantPrice, variantCount } = item.variants.reduce(
                    (variantAcc, variant) => {
                        return {
                            variantPrice: variantAcc.variantPrice + (variant.sellPrice + selectionPrice) * variant.quantity,
                            variantCount: variantAcc.variantCount + variant.quantity,
                        }
                    },
                    { variantPrice: 0, variantCount: 0 }
                )

                return {
                    price: acc.price + variantPrice,
                    count: acc.count + variantCount,
                }
            },
            { price: 0, count: 0 }
        )
        return { price, count }
    }, [items])

    const handleModalConfirm = async () => {
        if (isProcessing) return
        setIsInventoryModalOpen(false)
        setIsSubmitting(true)
        try {
            await processOrder()
        } finally {
            setIsSubmitting(false)
        }
    }

    const onCheckout = async () => {
        if (isProcessing) return
        setIsSubmitting(true)
        try {
            const ids = extractInventoryIds(items)
            const res = await checkInventoryStatus(ids)
            if (res.meta.requestStatus === 'fulfilled') {
                const { isAllActive } = res.payload
                if (isAllActive) {
                    await processOrder()
                } else {
                    setIsInventoryModalOpen(true)
                }
            }
        } finally {
            setIsSubmitting(false)
        }
    }

    const processOrder = async () => {
        const orderInput = {
            clientRequestId,
            cafeId: 1,
            isDineIn: diningMethod.value === DINING_METHOD.DineIn,
            tableId: tableId?.value,
            paymentType: paymentMethod.value,
            dishes: items.map(({ stallName, quantity, ...rest }) => ({
                ...rest,
                variants: rest.variants.map(v => ({
                    ...v,
                    inventory: undefined,
                    selections: undefined,
                })),
                selections: rest.selections.map(s => ({
                    ...s,
                    options: s.options.map(o => ({
                        ...o,
                        inventory: undefined,
                    })),
                })),
            })),
        }

        const res = await submitOrder(orderInput)
        if (res.meta.requestStatus === 'fulfilled') {
            if (paymentMethod.value === PAYMENT_METHOD.PayNow) {
                navigate(
                    ROUTES.Payment.root
                        .replace(':orderId', res.payload.orderId)
                        .replace(':paymentIntentId', res.payload.payment.paymentIntentId)
                        .replace(':clientSecret', res.payload.payment.clientSecret)
                )
            } else {
                navigate(ROUTES.Order.complete.replace(':orderId', res.payload.orderId), { replace: true })
            }
        } else {
            toast.error('Submit Order Failure')
            toast.error(res.payload)
        }
    }

    return (
        <div className="cart-footer">
            {isLoading && <Loader />}
            <div className="cart-footer-summary">
                <div className="items-container">
                    <h6 className="m-0">Total</h6>
                    <p>S$ {total.price.toFixed(2)}</p>
                </div>
            </div>
            <div className="cart-footer-summary">
                <div className="items-container">
                    <div className="d-flex align-items-center justify-content-between" onClick={() => setIsEditModalOpen(true)}>
                        <h6 className="m-0">{diningMethod.value === DINING_METHOD.DineIn ? 'Dine in' : diningMethod.value}</h6>
                        <i className="bx bx-edit bx-xs text-secondary ms-2 edit-icon"></i>
                    </div>
                    {diningMethod.value === DINING_METHOD.DineIn && <p>Table {tableId.value}</p>}
                </div>
            </div>
            {/* <div className="cart-footer-summary">
                <div className="items-container">
                    <div
                        className="d-flex align-items-center justify-content-between"
                        onClick={() => setIsPaymentMethodModalOpen(true)}
                    >
                        <h6 className="m-0">Payment Method</h6>
                        <i className="bx bx-edit bx-xs text-secondary ms-2 edit-icon"></i>
                    </div>
                    <p className={`${paymentMethod.value === PAYMENT_METHOD.PayNow ? 'text-info' : 'text-dark'}`}>
                        {paymentMethod.label}
                    </p>
                </div>
            </div> */}
            <button
                type="button"
                className="btn btn-primary btn-lg w-100 cart-footer-button"
                disabled={isProcessing}
                onClick={onCheckout}
            >
                {isLoading || isSubmitting ? 'Processing...' : 'Checkout'}
            </button>

            <DiningMethodEditModal
                isOpen={isEditModalOpen}
                toggle={() => setIsEditModalOpen(!isEditModalOpen)}
                currentDiningMethod={diningMethod}
                currentTableId={tableId}
                hasItems={items.length > 0}
            />

            {/* <PaymentMethodEditModal
                isOpen={isPaymentMethodModalOpen}
                toggle={() => setIsPaymentMethodModalOpen(!isPaymentMethodModalOpen)}
                currentPaymentMethod={paymentMethod}
                onPaymentMethodChange={setPaymentMethod}
            /> */}

            <InventoryWarningModal
                isOpen={isInventoryModalOpen}
                isProcessing={isProcessing}
                onCancel={() => setIsInventoryModalOpen(false)}
                onConfirm={handleModalConfirm}
                inventoryChanges={inventoryChanges}
            />
        </div>
    )
}

export default CartFooter
